import React from 'react';
import { GenericObject } from '@dxb-web-ui-framework/common/types';
import { useTranslation } from 'react-i18next';
import { getClientTimezone, stringToDate } from '@dxb-web-ui-framework/common/utils/Date';
import { Box, makeStyles, Typography } from '@dxb-web-ui-framework/design-system';
import { FilterList as FiltersIcon } from '@dxb-web-ui-framework/design-system/dist/icons';
import { TableGridConfig } from '../../../../content-composer/src/components/Generic/types';
import {
  formatAmountCellRenderer,
  stringToDateCellRenderer,
  stringWithPartiallyMaskValue,
  transferHistoryTransactionTypeCellRenderer,
  viewDetailsButtonRenderer,
} from '../../../../content-composer/src/components/Generic/TableGrid/tableGridCellRenderers/tableGridCellRenderers';
import {
  formatAmountTransformer,
  transactionStatusTransformer,
} from '../../../../content-composer/src/components/Generic/TableGrid/tableGridMobileListTransformers/tableGridMobileListTransformers';
import { TransactionOrderStatusChip } from '../TransactionOrderStatusChip';

const useStyles = makeStyles(() => ({
  root: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '15ch',
  },
}));

// TODO: get date format from the translation based on selected locale
const useConsolidatedTransactionsGridConfig = (
  onSelectListItem: (rowData: any) => void,
  onToggleDrawer?: Function,
  onToggleFilterDrawer?: () => void,
  downloadComponent?: React.FC,
): TableGridConfig => {
  const { t } = useTranslation();
  const locale = t('global.settings.date.locale');
  const dateFormat = t('global.settings.date.format');
  const timezone = getClientTimezone();
  const classes = useStyles();

  const gridConfig: TableGridConfig = {
    gridOptions: {
      ariaTitle: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.AriaTitle'),
      dataTestId: 'Transactions-table',
      noResultsMsg: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.NoResultsMsg'),
      padding: 'normal',
      size: 'medium',
      stickyHeader: false,
      collapseToList: true,
    },
    columnDefs: [
      {
        id: 'Creation date',
        field: 'orderTrxDate',
        label: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.TableHeadingCreationDate'),
        cellRenderer: stringToDateCellRenderer(dateFormat, locale, timezone),
      },
      {
        id: 'Value date',
        field: 'orderValueDate',
        label: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.TableHeadingValueDate'),
        cellRenderer: stringToDateCellRenderer(dateFormat, locale, timezone),
      },
      {
        id: 'Counterparty',
        field: 'beneficiaryName',
        label: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.TableHeadingCounterparty'),
        cellProps: {
          classes: { root: classes.root },
        },
      },
      {
        id: 'BIC',
        field: 'beneficiaryBic',
        label: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.TableHeadingBeneficiaryBic'),
      },
      {
        id: 'Bank',
        field: 'beneficiaryBank',
        label: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.TableHeadingBeneficiaryBank'),
      },
      {
        id: 'Status',
        field: 'status',
        label: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.TableHeadingStatus'),
        cellRenderer: transactionStatusTransformer,
      },
      {
        id: 'Payment details',
        field: 'orderComments',
        label: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.TableHeadingPaymentDetails'),
        cellProps: {
          classes: { root: classes.root },
        },
      },
      {
        id: 'Account',
        field: 'beneficiaryAccount',
        label: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.TableHeadingAccount'),
        cellRenderer: stringWithPartiallyMaskValue(4, 5),
      },
      // TODO use genericChipCellRenderer from develop branch for displaying chip
      {
        id: 'Type',
        field: 'orderDirection',
        label: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.TableHeadingTransactionType'),
        cellRenderer: (rowData: any, columnDef: any) =>
          transferHistoryTransactionTypeCellRenderer(rowData[columnDef.field], t),
      },
      {
        id: 'Transaction ID',
        field: 'orderId',
        label: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.TableHeadingTransactionId'),
      },
      {
        id: 'Amount',
        field: 'orderAmount',
        label: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.TableHeadingAmount'),
        columnHeaderCellProps: {
          align: 'right',
        },
        cellProps: {
          align: 'right',
        },
        cellRenderer: formatAmountCellRenderer(locale, true, 'currencyShortDescription'),
      },
    ],
    mobileListConfig: {
      listItemContents: [
        {
          label: 'beneficiaryName',
          value: 'orderTrxDate',
          valueAreaLabel: 'orderTrxDate',
          labelColor: 'textPrimary',
          labelSize: 'large',
          valueSize: 'small',
          valueColor: 'secondary',
          valueTransformer: (data: GenericObject) => (
            <Box mt={1}>
              {data.beneficiaryAccount}
              <Box mt={1}>
                {`${stringToDate({
                  date: data.orderTrxDate,
                  formatExp: dateFormat,
                  localeName: locale,
                  tz: timezone,
                })}`}
              </Box>
            </Box>
          ),
        },
        {
          label: 'orderAmount',
          value: 'status',
          valueAreaLabel: 'status',
          labelColor: 'textPrimary',
          labelSize: 'large',
          valueSize: 'small',
          valueColor: 'secondary',
          labelAlignment: 'right',
          valueAlignment: 'right',
          labelTransformer: (data: any) => (
            <Typography className={classes.root} variant='subtitle1' align='right'>
              {formatAmountTransformer('orderAmount', 'currencyShortDescription', 2, locale)(data)}
            </Typography>
          ),
          valueTransformer: (data: GenericObject) => (
            <Box display='flex' component='span' justifyContent='flex-end' mt={3}>
              <TransactionOrderStatusChip statusSerialNumber={data.status} />
            </Box>
          ),
        },
      ],
      listItemOnSelect: onSelectListItem,
    },
    // TODO: check permissions first to verify user has access to these actions (see how it is done in account item actions)
    // TODO: add the on click handler methods for the actions
    actions: [
      {
        dataTestId: 'download-transactions',
        variant: 'text',
        color: 'secondary',
        size: 'small',
        disabled: false,
        onClick: () => {},
        isIconButton: true,
        Component: downloadComponent ? downloadComponent : () => <></>,
        title: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.DownloadBtn'),
      },
      {
        dataTestId: 'transaction-filters',
        icon: <FiltersIcon color='secondary' />,
        variant: 'text',
        color: 'secondary',
        size: 'large',
        disabled: false,
        onClick: onToggleFilterDrawer ? onToggleFilterDrawer : () => {},
        isIconButton: true,
        title: t('consolidatedTransactionsPage.ConsolidatedTransactionsTable.FiltersBtn'),
      },
    ],
  };
  if (onToggleDrawer) {
    gridConfig.columnDefs.push({
      id: 'View Icon',
      field: 'actions',
      label: '',
      columnHeaderCellProps: {
        colSpan: 1,
        width: '5%',
        align: 'right',
      },
      cellProps: {
        colSpan: 1,
        width: '5%',
        align: 'right',
      },
      cellRenderer: viewDetailsButtonRenderer(
        onToggleDrawer,
        'transactionNo',
        false,
        t('consolidatedTransactionsHistoryPage.consolidatedTransactions.VisibilityIconBtnTitle'),
        t('consolidatedTransactionsHistoryPage.consolidatedTransactions.VisibilityIconBtnTitle'),
      ),
    });
  }
  return gridConfig;
};

export { useConsolidatedTransactionsGridConfig };
